import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { hasModifierKey } from '@angular/cdk/keycodes';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { isKey } from '@mtna/core-ts';
import { VariableSummary } from '@mtna/variable-service-core-ts';

import { CanLoad, CanTableEdit } from '../core/common-behaviors';
import { RdsVariableUtil } from '../core/utils';
import { RDS_DATA_DICTIONARY_ALL_COLUMNS, RDS_DATA_DICTIONARY_COLUMNS, RdsDataDictionaryTableColumn } from './column.model';
import { RDS_DICTIONARY_DEFAULT_COLUMNS, RDS_DICTIONARY_TABLE_MAX_COL_WIDTH, RDS_DICTIONARY_TABLE_MIN_COL_WIDTH } from './defaults.model';
import { _RdsDataDictionaryTableMixinBase } from './table-base.model';
import { RdsDataDictionaryToggleEvent } from './toggle-event.model';

/* eslint-disable @angular-eslint/no-inputs-metadata-property */
/* eslint-disable @angular-eslint/no-outputs-metadata-property */

/**
 * Custom mat-table used to display variables in a data-dictionary view.
 * Ability to select variables(rows).
 *
 * When editable, the checkbox column will be removed and the ability to select rows will be disabled.
 *
 * @author Will Davis <will.davis@mtna.us>
 */
@Component({
  selector: 'rds-c-data-dictionary-table',
  templateUrl: './data-dictionary-table.component.html',
  styleUrls: ['./data-dictionary-table.component.scss'],
  inputs: ['columnWidths', 'editable', 'resizableColumns', 'loading'],
  outputs: ['update', 'resizeColumn'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class RdsDataDictionaryTableComponent
  extends _RdsDataDictionaryTableMixinBase
  implements CanLoad, CanTableEdit<VariableSummary>, OnInit
{
  static ngAcceptInputType_columns: RdsDataDictionaryTableColumn[] | null | undefined;
  static ngAcceptInputType_disableViewDetails: BooleanInput;
  static ngAcceptInputType_uneditableCols: string[] | null | undefined;
  static ngAcceptInputType_editable: BooleanInput;
  static ngAcceptInputType_loading: BooleanInput;
  static ngAcceptInputType_toggledAreSelected: BooleanInput;
  static ngAcceptInputType_variables: VariableSummary[] | null | undefined;

  /**
   * All the column options to choose from. Defaults to:
   * `RDS_DATA_DICTIONARY_ALL_COLUMNS.slice()`
   */
  @Input() allColumnOptions: Array<RdsDataDictionaryTableColumn> = RDS_DATA_DICTIONARY_ALL_COLUMNS.slice();

  /**
   * The default, visible table columns to render. Defaults to:
   * `RDS_DATA_DICTIONARY_ALL_COLUMNS.slice(0, 5)`
   */
  @Input()
  get columns(): Array<RdsDataDictionaryTableColumn> {
    return this._columns;
  }
  set columns(cols: Array<RdsDataDictionaryTableColumn>) {
    this._columns = cols || [];
    // determine and set displayable columns for the table
    // sort by index, and use the property names as column IDs
    this.displayedColumns = !this.columns
      ? []
      : this.columns
          .slice()
          .sort((a, b) => a.index - b.index)
          .map((c) => c.property);
    if (this.resizableColumns) {
      this.initializeColumnWidths();
    }
  }
  private _columns: Array<RdsDataDictionaryTableColumn> = RDS_DICTIONARY_DEFAULT_COLUMNS;

  /**
   * Sets rows as unfocusable/unselectable, preventing 'viewDetails' emitting.
   */
  @Input() get disableViewDetails(): boolean {
    return this._disableViewDetails;
  }
  set disableViewDetails(value: boolean) {
    this._disableViewDetails = coerceBooleanProperty(value);
  }
  private _disableViewDetails = false;

  /**
   * IDs of columns that should remain uneditable in editable state.
   */
  @Input() get uneditableCols(): string[] {
    return this._uneditableCols;
  }
  set uneditableCols(d: string[]) {
    this._uneditableCols = d || [];
  }
  private _uneditableCols: string[] = [];

  /**
   * Whether the toggled variables represent the selected, or unselected.
   * For instance, if the user selects 100+ variables, and unselects 5,
   * the 'toggled' variables will be the 5 unselected & 'toggledAreSelected' will be false;
   * i.e.
   * user selects 95 out of 100 => toggledAreSelected: false, toggled.length: 5;;
   * user selects 5 out of 100 => toggledAreSelected: true, toggled.length: 5;
   */
  @Input()
  get toggledAreSelected(): boolean {
    return this._toggledAreSelected;
  }
  set toggledAreSelected(selected: boolean) {
    selected = coerceBooleanProperty(selected);
    this._toggledAreSelected = selected;
  }
  private _toggledAreSelected = true;

  /** The set of (un)selected variables toggled by the user */
  @Input()
  set toggledUris(variableUris: string[]) {
    this._toggledUris = {};
    variableUris.forEach((uri) => (this._toggledUris[uri] = true));
  }
  /** uri map of variables currently toggled */
  _toggledUris: { [uri: string]: boolean } = {};

  /** Variables to display in the table */
  @Input()
  get variables(): Array<VariableSummary> {
    return this._variables;
  }
  set variables(variables: Array<VariableSummary>) {
    this._variables = variables || [];
    this.checkAndUpdateFormArray();
  }
  private _variables: Array<VariableSummary> = [];

  /** Event emitted when the user toggles an individual variable selection */
  @Output() toggle = new EventEmitter<RdsDataDictionaryToggleEvent>();
  /** Event emitted when the user selects all visible variables */
  @Output() selectAll = new EventEmitter<VariableSummary[]>();
  /** Event emitted when the user deselects all visible variables */
  @Output() selectNone = new EventEmitter<VariableSummary[]>();
  /** Emitted to show additional variable details */
  @Output() viewDetails = new EventEmitter<VariableSummary>();
  /** Emitted to externally edit a variable. */
  @Output() editVariable = new EventEmitter<VariableSummary>();

  get areColumnsSet(): boolean {
    return !!this.columns;
  }

  get maxAutoColWidth(): number {
    return RDS_DICTIONARY_TABLE_MAX_COL_WIDTH;
  }

  get minColWidth(): number {
    return RDS_DICTIONARY_TABLE_MIN_COL_WIDTH;
  }

  constructor(cdr: ChangeDetectorRef, private _fb: FormBuilder) {
    super(cdr);
  }

  checkAndUpdateFormArray() {
    if (this.editable && this.variables.length) {
      this._formArray = this._fb.array(
        // Create a FormGroup for each VariableSummary
        this.variables.map((variable) => {
          const controls: { [key in keyof Partial<VariableSummary>]: FormControl } = {};
          // Create a FormControl for each column
          this.columns
            .filter((col) => col.property !== 'selected' && col.property !== 'hasClassification' && col.property !== 'editButton')
            .forEach((col) => {
              // We can safely cast and exclude the keys due to the filter above
              const prop = col.property as Exclude<RDS_DATA_DICTIONARY_COLUMNS, 'selected' | 'hasClassification' | 'editButton'>;
              controls[prop] = this._fb.control(variable[prop]);
            });
          return this._fb.group(controls);
        })
      );
    }
  }

  getLargestContentWidth(id: string): number {
    let contentWidth = 0;
    // Find the property in the variables & calculate the length (if it's a string)
    this.variables.forEach((v) => {
      if (isKey(v, id)) {
        const value = v[id];
        if (typeof value === 'string') contentWidth = Math.max(contentWidth, this.calculateCellWidth(value.length));
      }
    });
    return contentWidth;
  }

  /** Triggers table row selection.
   * We make use of 'keydown' because another component's keyup event (i.e. side panel toggle button) can leak
   * and be picked up by this component when a row has focus restored, thereby retriggering unwanted row selection
   *
   * Note: I think we won't need this once UIC-125 is done.
   */
  handleRowKeydown(event: KeyboardEvent, variable: VariableSummary) {
    switch (event.code) {
      /** See documentation for code values below
       * https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/code/code_values */
      case 'Enter':
        if (!hasModifierKey(event)) {
          event.preventDefault();
          this.handleRowSelection(variable);
        }
    }
  }

  handleRowSelection(variable: VariableSummary) {
    if (!this.editable) {
      this.viewDetails.emit(variable);
    }
  }

  handleSelectAll() {
    this.selectAll.emit(this.variables);
  }

  masterToggle(event: MatCheckboxChange) {
    event.checked ? this.handleSelectAll() : this.selectNone.emit(this.variables);
  }

  ngOnInit() {
    // If no columns were set, initialize with the default
    if (!this._columns) {
      // determine and set displayable columns for the table
      // sort by index, and use the property names as column IDs
      this.displayedColumns = !this.columns
        ? []
        : this.columns
            .slice()
            .sort((a, b) => a.index - b.index)
            .map((c) => c.property);
      if (this.resizableColumns) {
        this.initializeColumnWidths();
      }
    }
  }

  updateResource(previousVariable: VariableSummary, updatedVariable: VariableSummary) {
    this.variables = this.variables.map((v) => (RdsVariableUtil.compareVariables(v, updatedVariable) ? updatedVariable : v));
  }

  protected getColumnWidth(id: RDS_DATA_DICTIONARY_COLUMNS): number {
    switch (id) {
      case 'isMeasure':
      case 'isRequired':
      case 'isWeight':
        return 90;
      case 'dataType':
        return 100;
      case 'hasClassification':
        return 124;
      case 'label':
        return 200;
      case 'questionText':
        return 400;
      default:
        return super.getColumnWidth(id);
    }
  }
}
